import {observer} from "mobx-react";
import EditorPageLayout from "../../../../../../components/layouts/editor-page/editor-page-layout";
import {useNavigate, useOutletContext} from "react-router-dom";
import Card from "../../../../../../components/layouts/card/card";
import FontBody1 from "../../../../../../components/fonts/font-body1/font-body1";
import {FONT_COLOR, FONT_VARIANT} from "../../../../../../components/fonts/font-props";
import GridLineLayout from "../../../../../../components/layouts/grid-line-layout/grid-line-layout";
import GridLine from "../../../../../../components/layouts/grid-line-layout/grid-line/grid-line";
import GridLineItem
    from "../../../../../../components/layouts/grid-line-layout/grid-line/grid-line-item/grid-line-item";
import GridLineIndicators
    from "../../../../../../components/layouts/grid-line-layout/grid-line/grid-line-indicators/grid-line-indicators";
import GridLineButtons
    from "../../../../../../components/layouts/grid-line-layout/grid-line/grid-line-buttons/grid-line-buttons";
import {ButtonIcon} from "../../../../../../components/inputs/button-icon/button-icon";
import IconEdit from "../../../../../../components/icons/icon-edit/icon-edit";
import {Fragment, useEffect} from "react";
import ListLayout from "../../../../../../components/layouts/list-layout/list-layout";
import usePage from "../../../../../../components/layouts/base-page-layout/page-hook";
import currentPortalStore from "../../../../../../stores/current-portal-store";
import {FEATURES} from "../../../../../../models/Plan";
import {ExpandCallapseStore} from "../../../../../../hooks/expand-collapse-hook";
import PreviewLink from "../../../../../../components/preview-link/preview-link";
import FontBody2 from "../../../../../../components/fonts/font-body2/font-body2";
import dashboardSettingsStore from "../../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../../models/ProfileCapabilities";

const ContentSettingsBlockList = observer(() => {
    const expandCollapseStore = useOutletContext<ExpandCallapseStore>();

    const portalTranslationsCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.PORTAL_TRANSLATIONS);

    const Cards = [
        {
            title: 'Return Portal',
            instructions: 'Customize the content of your return portal.',
            isAllowed: true,
            previewLink: <PreviewLink disabled={!currentPortalStore.setting}
                                      href={currentPortalStore.setting?.returnsPortalPreviewLink}/>,
            lineGroups: [{
                title: '',
                lines: [
                    {title: 'Locate Order', pathname: 'locate-order', isAllowed: true},
                    {title: 'Policy Confirmation', pathname: 'policy-confirmation', isAllowed: true},
                    {title: 'Gift Returns', pathname: 'gift-returns', isAllowed: currentPortalStore.setting?.enableGiftReturns},
                    {title: 'Product Exchange', pathname: 'product-exchange', isAllowed: currentPortalStore.isFeatureAllowed(FEATURES.PRODUCT_EXCHANGE)},
                    {
						title: 'Select Items',
	                    pathname: 'select-items',
	                    isAllowed: portalTranslationsCapability.isSelectItemsScopeSupported
                    },
                    {
                        title: 'Refund Methods',
                        pathname: 'refund-methods',
                        isAllowed: portalTranslationsCapability.isRefundMethodsScopeSupported,
                    },
                ],
            }]
        },
        {
            title: 'Tracking Portal',
            instructions: 'Customize the content of your tracking portal.',
            isAllowed: currentPortalStore.isFeatureAllowed(FEATURES.ORDER_TRACKING),
            disabled: !currentPortalStore.isFeatureAllowed(FEATURES.ORDER_TRACKING),
            previewLink: <PreviewLink disabled={!currentPortalStore.setting}
                                      href={currentPortalStore.setting?.trackingPortalPreviewLink}/>,
            lineGroups: [
                {
                    title: '',
                    lines: [
                        {title: 'Locate Shipment', pathname: 'locate-shipment', isAllowed: true},
                        {title: 'General Texts', pathname: 'general-texts', isAllowed: true},
                        {title: 'Tracking Statuses', pathname: 'tracking-statuses', isAllowed: true},
                        {title: 'Warning and Error Messages', pathname: 'warnings-and-messages', isAllowed: true},
                    ],
                }
            ],
            disabledTooltip: {
                content: (
                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                        The tracking portal feature is <br/>
                        not included in your <br/>
                        subscription plan. To activate <br/>
                        this feature, contact support.
                    </FontBody2>
                )
            }
        }
    ];

    const navigate = useNavigate();
    const {setSubTitles} = usePage();

    useEffect(() => {
        setSubTitles(['Portal', 'Content']);
    }, [setSubTitles]);

    const onOptionClicked = (pathname: string) => {
        navigate({
            pathname: pathname
        });
    };

    return (
        <EditorPageLayout toolbar={{
            hasBack: false,
            expandAllButton: {
                expanded: expandCollapseStore.isAllExpanded(),
                onClick: expandCollapseStore.toggleAllExpanded
            }
        }}>
            <ListLayout>
                {Cards.map((card, index) => (
                    <Card
                        toolbar={{
                            title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{card.title}</FontBody1>,
                            content: card.previewLink
                        }}
                        key={index}
                        isExpanded={expandCollapseStore.getIsExpanded(card.title)}
                        disabled={card.disabled}
                        expandCallback={(v: boolean) => expandCollapseStore.setExpanded(card.title, v)}
                        disabledTooltip={card.disabledTooltip}
                    >
                        <>
                            <FontBody1 color={FONT_COLOR.PRIMARY3_50}>{card.instructions}</FontBody1>
                            {card.lineGroups.map((lineGroup, groupIndex) => (
                                <Fragment key={groupIndex}>
                                    {!!lineGroup.title.length &&
                                        <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                            {lineGroup.title}
                                        </FontBody1>}

                                    <GridLineLayout>
                                        {lineGroup.lines.filter(line => line.isAllowed).map((line, lineIndex) => (
                                            <GridLine
                                                key={lineIndex}
                                                onClick={() => onOptionClicked(line.pathname)}
                                                settings={{
                                                    columns: [{width: 2}, {width: 30, type: 'px'}, {
                                                        width: 60,
                                                        type: 'px'
                                                    }]
                                                }}
                                                columns={[
                                                    <GridLineItem>
                                                        <FontBody1 color={FONT_COLOR.PRIMARY2}
                                                                   variant={[FONT_VARIANT.BOLD]}>{line.title}</FontBody1>
                                                    </GridLineItem>,

                                                    <GridLineIndicators>
                                                    </GridLineIndicators>,

                                                    <GridLineButtons>
                                                        <ButtonIcon Component={IconEdit}
                                                                    onClick={() => onOptionClicked(line.pathname)}/>
                                                    </GridLineButtons>,
                                                ]}/>
                                        ))}
                                    </GridLineLayout>
                                </Fragment>
                            ))}
                        </>
                    </Card>
                ))}
            </ListLayout>
        </EditorPageLayout>
    )
});

export default ContentSettingsBlockList;